import { useEffect, useRef, useState } from 'react'

export interface MultiSelectOption {
  value: string
  label: string
}

// A generic checkbox-list dropdown with search, used for every stats filter (models,
// sites, languages) — replaces the single-value "Alle modellen" dropdown, which forced
// picking one model at a time and made comparing several impossible without repeated
// re-filtering. Search only matters once there are enough options to need it (many
// sites), but showing it unconditionally is simpler than a size-dependent prop, and
// harmless for a 3-item list.
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  const summary = selected.length === 0 ? `Alle ${label.toLowerCase()}` : `${selected.length} geselecteerd`

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg border border-line bg-card px-2 py-1 text-xs text-ink-soft hover:border-amber focus:outline-none focus:ring-2 focus:ring-amber"
      >
        {label}: {summary}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-line bg-card p-2 shadow-lg">
          {options.length > 6 && (
            <input
              type="text"
              autoFocus
              placeholder="Zoeken…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mb-2 w-full rounded-md border border-line bg-card px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            />
          )}
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && <li className="px-1 py-1 text-xs text-ink-soft">Geen resultaten.</li>}
            {filtered.map((o) => (
              <li key={o.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs text-ink hover:bg-line/30">
                  <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                  {o.label}
                </label>
              </li>
            ))}
          </ul>
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="mt-1 text-xs text-amber hover:underline">
              Wissen
            </button>
          )}
        </div>
      )}
    </div>
  )
}
